import { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../utils/apiService';
import { useUser } from '../contexts/UserContext';
import { isKycVerifiedFromApiResult, parseKycStatusPayload } from '../pages/kyc/kycComplianceData';

let inflight = null;
let cached = null;

export const invalidateKycVerifiedCache = () => {
  cached = null;
  inflight = null;
};

/**
 * Resolves affiliate KYC verified flag (session first, then API).
 */
export const useKycVerified = () => {
  const { userData, updateUserData } = useUser();
  const [kycVerified, setKycVerified] = useState(() =>
    Boolean(
      userData?.isVerified ||
        userData?.verified ||
        userData?.isAccountVerified ||
        userData?.isIdentityVerified ||
        userData?.isKycVerified
    )
  );
  const [loading, setLoading] = useState(!kycVerified);
  const mounted = useRef(true);

  const syncVerified = useCallback(
    (verified) => {
      setKycVerified(verified);
      if (verified && userData && !userData.isVerified) {
        updateUserData(
          { ...userData, isVerified: true, verified: true },
          JSON.parse(localStorage.getItem('userPermissions') || '[]')
        );
      }
    },
    [updateUserData, userData]
  );

  useEffect(() => {
    mounted.current = true;
    const fromUser = Boolean(
      userData?.isVerified ||
        userData?.verified ||
        userData?.isAccountVerified ||
        userData?.isIdentityVerified ||
        userData?.isKycVerified
    );
    if (fromUser) {
      setKycVerified(true);
      setLoading(false);
      return undefined;
    }

    if (cached != null) {
      setKycVerified(cached);
      setLoading(false);
      return undefined;
    }

    const run = async () => {
      setLoading(true);
      try {
        if (!inflight) {
          inflight = apiService.getKycStatus().finally(() => {
            inflight = null;
          });
        }
        const res = await inflight;
        if (!mounted.current) return;
        const parsed = parseKycStatusPayload(res?.data);
        const verified = Boolean(res?.success && isKycVerifiedFromApiResult(parsed));
        cached = verified;
        syncVerified(verified);
      } catch {
        if (mounted.current) setKycVerified(false);
      } finally {
        if (mounted.current) setLoading(false);
      }
    };

    run();
    return () => {
      mounted.current = false;
    };
  }, [userData, syncVerified]);

  return { kycVerified, loading, refresh: invalidateKycVerifiedCache };
};

export default useKycVerified;
